import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
  memo,
} from 'react';
import styled from 'styled-components';
import { VariableSizeGrid as Grid, areEqual } from 'react-window';
import ResizeObserver from 'rc-resize-observer';
import InfiniteLoader from 'react-window-infinite-loader';
import { Table, Skeleton, Empty } from 'antd';
import { TableProps } from 'antd/lib/table';
import { CheckboxProps } from 'antd/lib/checkbox';
import Checkbox from '../Checkbox';
import theme from '../theme';

import {
  StyledPrimaryHeaderRow,
  StyledPrimaryHeaderCell,
} from '../Table/StyledComponents';

const StyledContactListHeaderWrapper = styled.thead`
  position: relative;
  &:before {
    content: '';
    width: 100%;
    height: 55px;
    position: absolute;
    top: 0;
    left: 0;
    box-shadow: ${theme.getElevation('bottom')};
    pointer-events: none;
    z-index: 1;
  }
`;

export interface VirtualizedInfiniteTableProps<DataType = any>
  extends TableProps<DataType> {
  itemCount?: number;
  rowHeight?: (rowIndex: number) => number;
  loadMoreItems?: (startIndex: number, stopIndex: number) => Promise<void>;
  onItemClick?: (rowIndex: number, record?: any) => void;
  onSelect?: (
    selectedRowKeys: string[],
    selectedRows: Record<string, unknown>[],
  ) => void;
}

const VirtualizedInfiniteTable: React.FC<
  VirtualizedInfiniteTableProps
> = props => {
  // Refs
  const gridRef = useRef<any>();
  const infiniteLoaderRef = useRef<InfiniteLoader>();
  // State
  const [tableWidth, setTableWidth] = useState(0);
  const [tableHeight, setTableHeight] = useState(0);
  const [selectedMap, setSelectedMap] = useState<Record<string, any>>({});
  const [connectObject] = useState<any>(() => {
    const obj = {};
    Object.defineProperty(obj, 'scrollLeft', {
      set: (scrollLeft: number) => {
        gridRef?.current?.scrollTo({ scrollLeft });
      },
    });

    return obj;
  });
  // Props
  const {
    columns,
    loading,
    dataSource,
    itemCount,
    loadMoreItems,
    onChange,
    onSelect,
    onItemClick,
    rowHeight = () => 70,
  } = props;

  const onCellClickGen =
    (rowIndex: number, record?: any) => (e: React.MouseEvent) => {
      if (onItemClick) onItemClick(rowIndex, record);
    };

  const resetVirtualGrid = () => {
    gridRef?.current?.resetAfterIndices({
      columnIndex: 0,
      rowIndex: 0,
    });
  };
  // Use Effect
  useEffect(() => {
    setSelectedMap({});
  }, [loading]);
  useEffect(() => {
    const keys = Object.keys(selectedMap);
    const rows = Object.values(selectedMap);
    if (onSelect) onSelect(keys, rows);
  }, [selectedMap, onSelect]);
  useEffect(() => resetVirtualGrid, [tableWidth]);

  let mergedColumns: any[] = [...columns];
  // Add Checkbox Select Column
  if (onSelect) {
    const isSelectAllDisabled = itemCount > 10;

    const isIndeterminate =
      Object.keys(selectedMap).length > 0 &&
      Object.keys(selectedMap).length < dataSource.length;

    const isSelectedAll =
      dataSource.length &&
      dataSource.length === Object.keys(selectedMap).length;

    const onSelectAllChange: CheckboxProps['onChange'] = e => {
      let updated = {};
      const isChecked = e.target.checked;
      if (isChecked) {
        updated = dataSource.reduce(
          (acc, record) => ({
            ...acc,
            [record.id]: record,
          }),
          {},
        );
      }
      setSelectedMap(updated);
    };

    const checkboxRender = (text?: string, record?: any) =>
      record ? (
        <StyledCheckbox
          checked={!!selectedMap[record.id]}
          onClick={e => e.stopPropagation()}
          onChange={e => {
            const isChecked = e.target.checked;
            let updated;
            if (isChecked) {
              updated = { ...selectedMap, [record.id]: record };
            } else {
              updated = { ...selectedMap };
              delete updated[record.id];
            }
            setSelectedMap(updated);
          }}
        />
      ) : (
        <StyledCheckboxSkeleton />
      );

    const selectColumn = {
      title: (
        <StyledCheckbox
          disabled={isSelectAllDisabled}
          indeterminate={isIndeterminate}
          checked={isSelectedAll}
          onChange={onSelectAllChange}
        />
      ),
      dataIndex: 'key',
      width: 70,
      render: checkboxRender,
    };

    mergedColumns.unshift(selectColumn);
  }
  // Calculate Column Width
  const widthColumnCount = mergedColumns.filter(
    ({ width }: any) => !width,
  ).length;
  mergedColumns = mergedColumns.map((column: any) => {
    if (column.width) {
      return column;
    }

    return {
      ...column,
      width: Math.floor(tableWidth / widthColumnCount),
    };
  });
  // This is for cirtual grid as it has no row wrapper
  const animateAndSetHoverClass = (className: string, hover: boolean) => () => {
    const elements = [...document.getElementsByClassName(className)];
    const keyframes = [
      {
        backgroundColor: 'white',
      },
      {
        backgroundColor: theme.gray2,
      },
    ];
    const reverseKeyframes = [...keyframes].reverse();
    elements.forEach(ele => {
      ele.classList[hover ? 'add' : 'remove']('hover');
      ele.animate(hover ? keyframes : reverseKeyframes, 150);
    });
  };
  // Check Is Item Loaded Props for Infinite Loader
  const isItemLoaded = (index: number) => !!dataSource[index];
  // Reset Loader Cache and Scroll to top
  const resetTable = () => {
    infiniteLoaderRef?.current?.resetloadMoreItemsCache();
    gridRef?.current?.scrollToItem({
      rowIndex: 0,
      columnIndex: 0,
      align: 'start',
    });
  };

  const onSortChange: TableProps<any>['onChange'] = (...args) => {
    if (onChange) onChange(...args);
    resetTable();
  };

  const renderVirtualList = (
    rawData: any[],
    { scrollbarSize, ref, onScroll }: any,
  ) => {
    // eslint-disable-next-line no-param-reassign
    ref.current = connectObject;
    const totalHeight = rawData.length * 54;
    const totalWidth = mergedColumns.reduce((acc, { width }) => acc + width, 0);
    const itemCountOrLoading = loading ? 15 : itemCount;

    return (
      <InfiniteLoader
        isItemLoaded={isItemLoaded}
        itemCount={itemCountOrLoading}
        loadMoreItems={loadMoreItems}
        ref={infiniteLoaderRef}
      >
        {({ onItemsRendered, ref: ilGridRef }) => (
          <Grid
            ref={gr => {
              // @ts-ignore
              ilGridRef(gr);
              gridRef.current = gr;
            }}
            className="virtual-grid"
            onItemsRendered={({
              visibleRowStartIndex,
              visibleRowStopIndex,
              overscanRowStopIndex,
              overscanRowStartIndex,
            }) => {
              onItemsRendered({
                overscanStartIndex: overscanRowStartIndex,
                overscanStopIndex: overscanRowStopIndex,
                visibleStartIndex: visibleRowStartIndex,
                visibleStopIndex: visibleRowStopIndex,
              });
            }}
            columnCount={mergedColumns.length}
            columnWidth={(index: number) => {
              let { width } = mergedColumns[index];

              if (totalWidth < tableWidth) {
                const ratio = width / totalWidth;
                const responsiveWidth = Math.ceil(tableWidth * ratio);
                width = responsiveWidth;
              }

              return totalHeight > tableHeight &&
                index === mergedColumns.length - 1
                ? width - scrollbarSize - 25
                : width;
            }}
            height={tableHeight}
            rowCount={itemCountOrLoading}
            rowHeight={rowHeight}
            width={tableWidth}
            onScroll={({ scrollLeft }) => {
              onScroll({ scrollLeft });
            }}
          >
            {memo(({ columnIndex, rowIndex, style }: any) => {
              let content: JSX.Element = <>{'Loading...'}</>;
              const rowData = rawData[rowIndex];
              const className = useMemo(
                () => `virtual-table-row-${rowIndex}`,
                [rowIndex],
              );
              const onEnter = useCallback(
                animateAndSetHoverClass(className, true),
                [className],
              );
              const onLeave = useCallback(
                animateAndSetHoverClass(className, false),
                [className],
              );

              const onClick = useCallback(onCellClickGen(rowIndex, rowData), [
                rowIndex,
                rowData,
              ]);

              if (rowData) {
                const record = rowData;
                content = record[mergedColumns[columnIndex].dataIndex];
                if (mergedColumns[columnIndex].render) {
                  content = mergedColumns[columnIndex].render(
                    content,
                    record,
                    rowIndex,
                  );
                }
              } else if (mergedColumns[columnIndex].render) {
                content = mergedColumns[columnIndex].render(
                  null,
                  null,
                  rowIndex,
                );
              }
              return (
                <StyledPrimaryBodyCell
                  isClickable={!!onItemClick}
                  onMouseLeave={onLeave}
                  onMouseEnter={onEnter}
                  className={className}
                  isLast={columnIndex === mergedColumns.length - 1}
                  style={style as React.CSSProperties}
                  onClick={onClick}
                >
                  {content}
                </StyledPrimaryBodyCell>
              );
            }, areEqual)}
          </Grid>
        )}
      </InfiniteLoader>
    );
  };

  return (
    <ResizeObserver
      onResize={({ width, height }) => {
        setTableWidth(width);
        setTableHeight(height);
      }}
    >
      <StyledWrapper>
        <Table
          {...props}
          loading={loading}
          style={{ tableLayout: 'auto' }}
          showSorterTooltip={false}
          className="virtual-table"
          columns={mergedColumns}
          pagination={false}
          onChange={onSortChange}
          scroll={{
            x: tableWidth,
            y: tableHeight,
            scrollToFirstRowOnChange: false,
          }}
          components={{
            header: {
              wrapper: StyledContactListHeaderWrapper,
              row: StyledPrimaryHeaderRow,
              cell: StyledPrimaryHeaderCell,
            },
            body:
              dataSource.length || loading
                ? renderVirtualList
                : () => (
                    <StyledEmptyWrapper>
                      <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
                    </StyledEmptyWrapper>
                  ),
          }}
        />
      </StyledWrapper>
    </ResizeObserver>
  );
};

export default VirtualizedInfiniteTable;

const StyledPrimaryBodyCell = styled.div<{
  isLast?: boolean;
  isClickable?: boolean;
}>`
  display: flex;
  align-items: center;
  padding: 12px 16px;
  border-bottom: 1px solid ${theme.gray4};
  flex-direction: row;
  box-sizing: border-box;
  ${({ isClickable }) => (isClickable ? 'cursor: pointer;' : '')}
  &.hover {
    background-color: ${theme.gray2};
  }
`;

// Calculate Height by subtracting fixed amount for horizontal scroll, not using percentage
const StyledWrapper = styled.div`
  display: flex;
  min-height: 0;
  height: calc(100% - 55px);
  max-height: calc(100% - 55px);
`;

const StyledCheckboxSkeleton = styled(Skeleton.Avatar).attrs({
  shape: 'square',
  size: 'small',
  active: true,
})`
  display: flex;
  justify-content: center;
  width: 100%;
`;

const StyledCheckbox = styled(Checkbox)`
  display: flex;
  justify-content: center;
  min-width: 100%;
`;

const StyledEmptyWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 50vh;
  max-height: 100%;
`;
